import { translateError } from '@kostbar/i18n'
import { captureException } from '@kostbar/monitoring'
import { ApiError } from './types'
import { useAuthStore } from '@/features/auth/store/auth-store'

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

/**
 * Single API request function with automatic auth header injection,
 * 401 retry with token refresh, and error mapping.
 */
export async function apiRequest<T>(
  endpoint: string,
  options?: RequestInit & { skipAuth?: boolean },
): Promise<T> {
  const { accessToken, refreshAccessToken, logout } = useAuthStore.getState()

  // Helper to make the actual request
  const makeRequest = async (token: string | null): Promise<Response> => {
    // A FormData body must set its own Content-Type: the boundary is generated by the browser, and
    // forcing application/json here leaves the server unable to parse the upload at all.
    const isFormData =
      typeof FormData !== 'undefined' && options?.body instanceof FormData
    const headers: Record<string, string> = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(options?.headers as Record<string, string>),
    }

    // Inject Authorization header if token is available and not explicitly skipped
    if (token && !options?.skipAuth) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const url = endpoint.startsWith('http')
      ? endpoint
      : `${API_BASE_URL}${endpoint}`

    return fetch(url, {
      ...options,
      headers,
    })
  }

  // Skip retry logic for auth endpoints (login, refresh)
  const isAuthEndpoint = endpoint.includes('/api/auth/')

  // First attempt with current token
  let response = await makeRequest(accessToken)

  // If 401 and not an auth endpoint, try refreshing token once
  if (response.status === 401 && !isAuthEndpoint) {
    try {
      await refreshAccessToken()
      const newToken = useAuthStore.getState().accessToken
      response = await makeRequest(newToken)
    } catch (refreshError) {
      // Refresh failed, logout user
      logout()
      const error = new ApiError(
        401,
        'session_expired',
        'Session expired. Please log in again.',
      )
      captureException(error, {
        tags: { api_status: '401', error_code: 'session_expired' },
      })
      throw error
    }
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T
  }

  // Parse response body
  const contentType = response.headers.get('content-type')
  let data: unknown
  let errorCode: string | undefined

  if (contentType?.includes('application/json')) {
    data = await response.json()
    // Check if backend returned an error code in the response body
    if (typeof data === 'object' && data !== null && 'code' in data) {
      errorCode = (data as { code?: string }).code
    }
  } else {
    data = await response.text()
  }

  // Handle error responses
  if (!response.ok) {
    const message = translateError(errorCode, response.status)
    const error = new ApiError(response.status, errorCode, message, data)
    captureException(error, {
      tags: { api_status: String(response.status), error_code: errorCode },
      extra: { endpoint, data },
    })
    throw error
  }

  return data as T
}
